import { http } from "@/api/http";
import { sanitizeExternalHttpUrl } from "@/utils/externalUrl";
import { assertUnsignedDecimal } from "@/utils/unsignedDecimal";

const STRIPE_CHECKOUT_HOSTS = ["checkout.stripe.com"] as const;
const STRIPE_PORTAL_HOSTS = ["billing.stripe.com"] as const;

export interface BillingEntitlement {
  plan: "enterprise" | "free" | "pro";
  subscription_status: string;
  free_token_limit: number;
  free_tokens_used: string;
  free_tokens_remaining: number;
  unlimited: boolean;
  current_period_start: string | null;
  current_period_end: string | null;
  grace_period_end: string | null;
  cancel_at_period_end: boolean;
}

export interface BillingPayment {
  kind: "subscription_invoice" | "support_tip";
  id: string;
  paid_at: string | null;
  item: string;
  plan: string;
  amount_paid: number;
  currency: string;
  status: string;
}

export type BillingUsageMetricKey =
  | "ai_credits"
  | "applications"
  | "browser_runtime"
  | "storage";
export type BillingUsageMeasurementState = "available" | "collecting" | "unavailable";
export type BillingUsageWindow = "billing_period" | "current" | "lifetime";
export type BillingUsageEnforcement = "existing" | "informational" | "none";
export type BillingUsagePeriodKind = "calendar_month" | "subscription";

export interface BillingUsagePeriod {
  kind: BillingUsagePeriodKind;
  start: string;
  end: string;
}

interface BillingUsageMetricBase {
  state: BillingUsageMeasurementState;
  used: string | null;
  included: string | null;
  unlimited: boolean;
  period_start: string | null;
  period_end: string | null;
  resets_at: string | null;
  tracked_from: string | null;
  definition: string;
}

export interface ApplicationsBillingUsageMetric extends BillingUsageMetricBase {
  key: "applications";
  state: "available" | "unavailable";
  unit: "applications";
  window: "billing_period";
  enforcement: "none";
  complete_for_period?: never;
}

export interface AiCreditsBillingUsageMetric extends BillingUsageMetricBase {
  key: "ai_credits";
  state: "available" | "unavailable";
  unit: "credits";
  window: "lifetime";
  enforcement: "existing" | "none";
  complete_for_period?: never;
}

export interface BrowserRuntimeBillingUsageMetric extends BillingUsageMetricBase {
  key: "browser_runtime";
  unit: "seconds";
  window: "billing_period";
  enforcement: "informational";
  complete_for_period: boolean;
}

export interface StorageBillingUsageMetric extends BillingUsageMetricBase {
  key: "storage";
  state: "available" | "unavailable";
  unit: "bytes";
  window: "current";
  enforcement: "informational";
  complete_for_period?: never;
}

export type BillingUsageMetric =
  | AiCreditsBillingUsageMetric
  | ApplicationsBillingUsageMetric
  | BrowserRuntimeBillingUsageMetric
  | StorageBillingUsageMetric;

export interface BillingUsageSnapshot {
  generated_at: string;
  period: BillingUsagePeriod;
  metrics: [
    ApplicationsBillingUsageMetric,
    AiCreditsBillingUsageMetric,
    BrowserRuntimeBillingUsageMetric,
    StorageBillingUsageMetric,
  ];
}

export interface BillingUsageEnvelope {
  usage: BillingUsageSnapshot;
}

const BILLING_USAGE_ERROR = "Billing usage response is invalid.";
const BILLING_USAGE_KEYS: readonly BillingUsageMetricKey[] = [
  "applications",
  "ai_credits",
  "browser_runtime",
  "storage",
];
const BILLING_USAGE_ENVELOPE_KEYS = ["usage"] as const;
const BILLING_USAGE_SNAPSHOT_KEYS = ["generated_at", "period", "metrics"] as const;
const BILLING_USAGE_PERIOD_KEYS = ["kind", "start", "end"] as const;
const BILLING_USAGE_METRICS_TUPLE_KEYS = ["0", "1", "2", "3", "length"] as const;
const BILLING_USAGE_METRIC_KEYS = [
  "key",
  "state",
  "used",
  "included",
  "unlimited",
  "unit",
  "window",
  "period_start",
  "period_end",
  "resets_at",
  "tracked_from",
  "definition",
  "enforcement",
] as const;
const BROWSER_USAGE_METRIC_KEYS = [
  ...BILLING_USAGE_METRIC_KEYS,
  "complete_for_period",
] as const;
const ISO_TIMESTAMP =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,6}))?(?:(Z)|([+-])(\d{2}):(\d{2}))$/;

function invalidBillingUsage(): never {
  throw new Error(BILLING_USAGE_ERROR);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function hasExactOwnKeys(
  value: object,
  expected: readonly string[],
): boolean {
  const actual = Reflect.ownKeys(value);
  return actual.length === expected.length
    && actual.every((key) => typeof key === "string" && expected.includes(key));
}

function isLeapYear(year: number): boolean {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
}

function daysInMonth(year: number, month: number): number {
  if (month === 2) return isLeapYear(year) ? 29 : 28;
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
}

function parseIsoTimestamp(value: unknown): bigint | null {
  if (typeof value !== "string") return null;
  const match = ISO_TIMESTAMP.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = Number(match[6]);
  const fractionMicros = Number((match[7] ?? "").padEnd(6, "0"));
  const offsetHour = match[8] === "Z" ? 0 : Number(match[10]);
  const offsetMinute = match[8] === "Z" ? 0 : Number(match[11]);
  if (
    year < 1
    || month < 1
    || month > 12
    || day < 1
    || day > daysInMonth(year, month)
    || hour > 23
    || minute > 59
    || second > 59
    || offsetHour > 23
    || offsetMinute > 59
  ) {
    return null;
  }

  const local = new Date(0);
  local.setUTCFullYear(year, month - 1, day);
  local.setUTCHours(hour, minute, second, 0);
  const offsetDirection = match[9] === "-" ? -1 : 1;
  const offsetMinutes = offsetDirection * ((offsetHour * 60) + offsetMinute);
  return (BigInt(local.getTime()) * 1_000n)
    + BigInt(fractionMicros)
    - (BigInt(offsetMinutes) * 60_000_000n);
}

function isIsoTimestamp(value: unknown): value is string {
  return parseIsoTimestamp(value) !== null;
}

function isNullableTimestamp(value: unknown): value is string | null {
  return value === null || isIsoTimestamp(value);
}

function isUnsignedDecimal(value: unknown): value is string {
  if (typeof value !== "string") return false;
  try {
    assertUnsignedDecimal(value);
    return true;
  } catch {
    return false;
  }
}

function isNullableQuantity(value: unknown): value is string | null {
  return value === null || isUnsignedDecimal(value);
}

function validatePeriod(value: unknown): BillingUsagePeriod {
  if (!isRecord(value) || !hasExactOwnKeys(value, BILLING_USAGE_PERIOD_KEYS)) {
    invalidBillingUsage();
  }
  const { end, kind, start } = value;
  const startInstant = parseIsoTimestamp(start);
  const endInstant = parseIsoTimestamp(end);
  if (
    (kind !== "calendar_month" && kind !== "subscription")
    || startInstant === null
    || endInstant === null
    || startInstant >= endInstant
  ) {
    invalidBillingUsage();
  }
  return value as unknown as BillingUsagePeriod;
}

function validateMetricCommon(metric: Record<string, unknown>): void {
  const { definition, enforcement, included, state, tracked_from: trackedFrom, unlimited, used, window } = metric;
  if (
    (state !== "available" && state !== "collecting" && state !== "unavailable")
    || (window !== "billing_period" && window !== "current" && window !== "lifetime")
    || (enforcement !== "existing" && enforcement !== "informational" && enforcement !== "none")
    || !isNullableQuantity(used)
    || !isNullableQuantity(included)
    || typeof unlimited !== "boolean"
    || !isNullableTimestamp(metric.period_start)
    || !isNullableTimestamp(metric.period_end)
    || !isNullableTimestamp(metric.resets_at)
    || !isNullableTimestamp(trackedFrom)
    || typeof definition !== "string"
    || definition.trim().length === 0
    || (state === "unavailable" ? used !== null : used === null)
    || (unlimited ? included !== null : included === null)
  ) {
    invalidBillingUsage();
  }
}

function validateMetricWindow(metric: Record<string, unknown>, period: BillingUsagePeriod): void {
  if (metric.window === "billing_period") {
    if (
      metric.period_start !== period.start
      || metric.period_end !== period.end
      || metric.resets_at !== period.end
    ) {
      invalidBillingUsage();
    }
    return;
  }
  if (metric.period_start !== null || metric.period_end !== null || metric.resets_at !== null) {
    invalidBillingUsage();
  }
}

function validateMetricContract(metric: Record<string, unknown>, period: BillingUsagePeriod): void {
  const expectedKeys = metric.key === "browser_runtime"
    ? BROWSER_USAGE_METRIC_KEYS
    : BILLING_USAGE_METRIC_KEYS;
  if (!hasExactOwnKeys(metric, expectedKeys)) invalidBillingUsage();
  validateMetricCommon(metric);
  validateMetricWindow(metric, period);
  const hasCompleteFlag = Object.prototype.hasOwnProperty.call(metric, "complete_for_period");

  if (metric.key === "applications") {
    if (
      metric.state === "collecting"
      || metric.unit !== "applications"
      || metric.window !== "billing_period"
      || metric.enforcement !== "none"
      || metric.included !== null
      || metric.unlimited !== true
      || metric.tracked_from !== null
      || hasCompleteFlag
    ) {
      invalidBillingUsage();
    }
    return;
  }

  if (metric.key === "ai_credits") {
    if (
      metric.state === "collecting"
      || metric.unit !== "credits"
      || metric.window !== "lifetime"
      || metric.tracked_from !== null
      || hasCompleteFlag
      || (metric.unlimited ? metric.enforcement !== "none" : metric.enforcement !== "existing")
    ) {
      invalidBillingUsage();
    }
    return;
  }

  if (metric.key === "browser_runtime") {
    const trackedFromInstant = parseIsoTimestamp(metric.tracked_from);
    const periodStartInstant = parseIsoTimestamp(period.start);
    if (
      metric.unit !== "seconds"
      || metric.window !== "billing_period"
      || metric.enforcement !== "informational"
      || metric.unlimited !== false
      || !hasCompleteFlag
      || typeof metric.complete_for_period !== "boolean"
      || (metric.state === "collecting" && metric.complete_for_period)
      || (metric.state === "available" && !metric.complete_for_period)
      || (metric.state === "unavailable" && metric.complete_for_period)
      || (
        metric.state === "available"
        && (
          trackedFromInstant === null
          || periodStartInstant === null
          || trackedFromInstant > periodStartInstant
        )
      )
    ) {
      invalidBillingUsage();
    }
    return;
  }

  if (
    metric.key !== "storage"
    || metric.state === "collecting"
    || metric.unit !== "bytes"
    || metric.window !== "current"
    || metric.enforcement !== "informational"
    || metric.unlimited !== false
    || metric.tracked_from !== null
    || hasCompleteFlag
  ) {
    invalidBillingUsage();
  }
}

export function parseBillingUsageResponse(value: unknown): BillingUsageSnapshot {
  if (
    !isRecord(value)
    || !hasExactOwnKeys(value, BILLING_USAGE_ENVELOPE_KEYS)
    || !isRecord(value.usage)
    || !hasExactOwnKeys(value.usage, BILLING_USAGE_SNAPSHOT_KEYS)
  ) {
    invalidBillingUsage();
  }
  const usage = value.usage;
  if (!isIsoTimestamp(usage.generated_at)) invalidBillingUsage();
  const period = validatePeriod(usage.period);
  if (
    !Array.isArray(usage.metrics)
    || usage.metrics.length !== BILLING_USAGE_KEYS.length
    || !hasExactOwnKeys(usage.metrics, BILLING_USAGE_METRICS_TUPLE_KEYS)
  ) {
    invalidBillingUsage();
  }
  const metrics = usage.metrics;
  for (const [index, expectedKey] of BILLING_USAGE_KEYS.entries()) {
    const metric = metrics[index];
    if (!isRecord(metric) || metric.key !== expectedKey) invalidBillingUsage();
    validateMetricContract(metric, period);
  }
  return usage as unknown as BillingUsageSnapshot;
}

export async function getBillingEntitlement(): Promise<BillingEntitlement> {
  const response = await http.get<{ entitlement: BillingEntitlement }>("/billing/entitlement");
  return response.data.entitlement;
}

export async function getBillingHistory(): Promise<BillingPayment[]> {
  const response = await http.get<{ payments: BillingPayment[] }>("/billing/history");
  return response.data.payments;
}

export async function fetchBillingUsage(): Promise<BillingUsageSnapshot> {
  const response = await http.get<unknown>("/billing/usage");
  return parseBillingUsageResponse(response.data);
}

function stripeRedirectUrl(value: unknown, allowedHosts: readonly string[]): string {
  const url = sanitizeExternalHttpUrl(value, allowedHosts);
  if (!url) throw new Error("Billing provider returned an unsafe redirect URL.");
  return url;
}

export async function startProCheckout(): Promise<string> {
  const response = await http.post<{ url: string }>("/billing/checkout", {});
  return stripeRedirectUrl(response.data.url, STRIPE_CHECKOUT_HOSTS);
}

export async function openCustomerPortal(): Promise<string> {
  const response = await http.post<{ url: string }>("/billing/portal", {});
  return stripeRedirectUrl(response.data.url, STRIPE_PORTAL_HOSTS);
}
